import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import PostAddOutlinedIcon from '@mui/icons-material/PostAddOutlined';
import {
  Box,
  Button,
  ButtonBase,
  Divider,
  IconButton,
  Link as MuiLink,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { type ReactNode, useRef, useState } from 'react';
import { Link } from 'react-router';

import { elevatorSummary } from '~/components/tasks/ElevatorNames';
import { StatusChip } from '~/components/tasks/StatusChip';
import {
  avatarColor,
  CATEGORY_LABEL,
  formatDate,
  formatShortDate,
  initials,
  isFollowUpOverdue,
  isoDaysFromToday,
  QUICK_FOLLOW_UPS,
} from '~/components/tasks/taskStyle';
import type { TaskDetail, TaskStatus } from '~/data/generated/model';
import { BUTTON_SX, pageBandColor, SURFACE_RADIUS } from '~/theme';
import { buildUrlForProperty } from '~/utils/urlBuilder';

function TaskAvatar({ id, name }: { id?: string; name?: string }) {
  return (
    <Box
      component="span"
      sx={{
        width: 26,
        height: 26,
        flex: 'none',
        borderRadius: '50%',
        bgcolor: avatarColor(id),
        color: '#fff',
        fontSize: 10,
        fontWeight: 700,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {initials(name)}
    </Box>
  );
}

const FOLLOW_UP_INFO =
  'On this date the task shows in Needs Attention, then as overdue. Once you have checked it, set the next follow-up or clear it to take it off the list.';

function Fact({ label, info, children }: { label: string; info?: string; children: ReactNode }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        component="div"
        sx={{ mb: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}
      >
        {label}
        {info ? (
          <Tooltip disableInteractive title={info}>
            <InfoOutlined
              aria-label={`About ${label}`}
              sx={{ fontSize: 14, color: 'text.disabled' }}
            />
          </Tooltip>
        ) : null}
      </Typography>
      <Box sx={{ fontSize: 14, fontWeight: 500, color: '#1b1f24' }}>{children}</Box>
    </Box>
  );
}

function StatusMenu({
  task,
  statuses,
  disabled,
  onChange,
}: {
  task: TaskDetail;
  statuses: readonly TaskStatus[];
  disabled: boolean;
  onChange: (statusId: string) => void;
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  return (
    <>
      <ButtonBase
        aria-label={`Status: ${task.status.name}`}
        aria-haspopup="menu"
        disabled={disabled}
        onClick={(event) => setAnchor(event.currentTarget)}
        sx={{ borderRadius: '12px', display: 'inline-flex', alignItems: 'center' }}
      >
        <StatusChip status={task.status} />
        <ArrowDropDownIcon fontSize="small" sx={{ color: 'text.secondary' }} />
      </ButtonBase>
      <Menu anchorEl={anchor} open={anchor !== null} onClose={() => setAnchor(null)}>
        {statuses.map((status) => (
          <MenuItem
            key={status.id}
            selected={status.id === task.status.id}
            onClick={() => {
              setAnchor(null);
              if (status.id !== task.status.id) onChange(status.id);
            }}
          >
            <StatusChip status={status} size="small" />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

const isCompleteDate = (value: string) => /^\d{4}-\d{2}-\d{2}$/.test(value) && value >= '1900';

// Typing fires a change per keystroke (year 0002, 0020…), so typed dates wait for
// Enter or blur; a pick from the calendar saves straight away.
function PickDateField({
  value,
  onPick,
}: {
  value: string | null;
  onPick: (value: string) => void;
}) {
  const [draft, setDraft] = useState(value ?? '');
  const typing = useRef(false);
  const sent = useRef(value);
  const send = (next: string) => {
    if (!isCompleteDate(next) || next === sent.current) return;
    sent.current = next;
    onPick(next);
  };
  return (
    <TextField
      type="date"
      size="small"
      label="Pick a date"
      value={draft}
      onKeyDown={(event) => {
        typing.current = true;
        if (event.key === 'Enter') send(draft);
      }}
      onChange={(event) => {
        const next = event.target.value;
        setDraft(next);
        if (!typing.current) send(next);
      }}
      onBlur={() => {
        typing.current = false;
        send(draft);
      }}
      slotProps={{ inputLabel: { shrink: true } }}
    />
  );
}

function FollowUpMenu({
  task,
  disabled,
  onChange,
}: {
  task: TaskDetail;
  disabled: boolean;
  onChange: (followUpDate: string | null) => void;
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const overdue = isFollowUpOverdue(task);
  const current = task.followUpDate
    ? `${formatShortDate(task.followUpDate)}${overdue ? ' · overdue' : ''}`
    : 'Not set';
  const pick = (value: string | null) => {
    setAnchor(null);
    if (value !== task.followUpDate) onChange(value);
  };
  return (
    <>
      <ButtonBase
        aria-label={`Follow-up: ${current}`}
        aria-haspopup="menu"
        disabled={disabled}
        onClick={(event) => setAnchor(event.currentTarget)}
        sx={{
          borderRadius: '4px',
          display: 'inline-flex',
          alignItems: 'center',
          font: 'inherit',
          color: overdue ? '#c62828' : 'inherit',
        }}
      >
        {current}
        <ArrowDropDownIcon fontSize="small" sx={{ color: 'text.secondary' }} />
      </ButtonBase>
      <Menu anchorEl={anchor} open={anchor !== null} onClose={() => setAnchor(null)}>
        {QUICK_FOLLOW_UPS.map(([label, days]) => {
          const date = isoDaysFromToday(days);
          return (
            <MenuItem key={label} onClick={() => pick(date)}>
              <ListItemText secondary={formatShortDate(date)}>{label}</ListItemText>
            </MenuItem>
          );
        })}
        <Box sx={{ px: 2, py: 1 }} onKeyDown={(event) => event.stopPropagation()}>
          <PickDateField value={task.followUpDate} onPick={pick} />
        </Box>
        {task.followUpDate ? <Divider /> : null}
        {task.followUpDate ? (
          <MenuItem onClick={() => pick(null)}>
            <ListItemText secondary="Also removes it from Needs Attention">
              Clear follow-up
            </ListItemText>
          </MenuItem>
        ) : null}
      </Menu>
    </>
  );
}

type Props = {
  task: TaskDetail;
  statuses: readonly TaskStatus[];
  busy: boolean;
  backTo: string | null;
  aiSummary: ReactNode;
  onStatusChange: (statusId: string) => void;
  onFollowUpChange: (followUpDate: string | null) => void;
  onEdit: () => void;
  onLogUpdate: () => void;
  compact?: boolean;
  footer?: ReactNode;
};

export function TaskHeader({
  task,
  statuses,
  busy,
  backTo,
  aiSummary,
  onStatusChange,
  onFollowUpChange,
  onEdit,
  onLogUpdate,
  compact = false,
  footer,
}: Props) {
  const allCars = task.cars.map((car) => car.name).join(', ');

  const statusLine = (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '8px',
        fontSize: 13,
        color: 'text.secondary',
      }}
    >
      <StatusChip status={task.status} />
      <MuiLink component={Link} to={buildUrlForProperty(task.propertyId)} underline="hover">
        {task.propertyName}
      </MuiLink>
      {task.portfolio ? (
        <>
          <span aria-hidden>›</span>
          <span>{task.portfolio.name}</span>
        </>
      ) : null}
    </Box>
  );

  const idLine = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', mt: 1 }}>
      <Box component="span" sx={{ font: "12px 'Roboto Mono', monospace", color: '#666' }}>
        {task.displayId}
      </Box>
      <Box component="span" sx={{ fontSize: 12, color: '#666' }}>
        {CATEGORY_LABEL[task.category]} · opened {formatDate(task.createdAt)}
      </Box>
    </Box>
  );

  const title = (
    <Typography
      variant={compact ? 'h6' : 'h5'}
      component="h1"
      sx={{ mt: compact ? 0.5 : 1, textWrap: 'pretty' }}
    >
      {task.title}
    </Typography>
  );

  const actions = (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', ml: -0.5 }}>
      <Button size="small" startIcon={<EditOutlinedIcon />} onClick={onEdit} sx={BUTTON_SX}>
        Edit task
      </Button>
      <Button size="small" startIcon={<PostAddOutlinedIcon />} onClick={onLogUpdate} sx={BUTTON_SX}>
        Log update
      </Button>
    </Box>
  );

  const facts = (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        gap: 2,
        mt: compact ? 2 : 3,
      }}
    >
      <Fact label="Owner">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <TaskAvatar id={task.assignee?.id} name={task.assignee?.name} />
          {task.assignee?.name ?? 'Unassigned'}
        </Box>
      </Fact>
      <Fact label="Status">
        <StatusMenu
          task={task}
          statuses={statuses}
          disabled={busy || statuses.length === 0}
          onChange={onStatusChange}
        />
      </Fact>
      <Fact label="Follow-up" info={FOLLOW_UP_INFO}>
        <FollowUpMenu task={task} disabled={busy} onChange={onFollowUpChange} />
      </Fact>
      <Fact label="Elevators">
        <Tooltip title={task.cars.length > 2 ? allCars : ''}>
          <span>{elevatorSummary(task.cars)}</span>
        </Tooltip>
      </Fact>
      <Fact label="Provider">{task.provider?.name ?? 'No provider on file'}</Fact>
    </Box>
  );

  const description = task.description ? (
    <Box
      sx={{
        mt: 2,
        fontSize: 14,
        color: '#333',
        whiteSpace: 'pre-wrap',
        textWrap: 'pretty',
      }}
    >
      {task.description}
    </Box>
  ) : null;

  if (compact) {
    return (
      <Box
        component="section"
        aria-label="Task summary"
        px={2}
        py={2}
        sx={{ bgcolor: pageBandColor }}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          columnGap={2}
          rowGap={1}
        >
          {statusLine}
          {actions}
        </Box>
        {idLine}
        {title}
        <Box mt={2}>{aiSummary}</Box>
        {facts}
        {description}
        {footer}
      </Box>
    );
  }

  return (
    <Box
      component="section"
      aria-label="Task summary"
      px={{ xs: 2, md: 3 }}
      py={{ xs: 2, md: 3 }}
      sx={{
        bgcolor: pageBandColor,
        borderBottom: 1,
        borderColor: 'divider',
        borderTopLeftRadius: SURFACE_RADIUS,
        borderTopRightRadius: SURFACE_RADIUS,
      }}
    >
      <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        {backTo ? (
          <Tooltip title="Back to tasks">
            <IconButton
              component={Link}
              to={backTo}
              aria-label="Back to tasks"
              size="small"
              sx={{ ml: -1 }}
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : null}
        <Box sx={{ flex: '1 1 420px', minWidth: 0 }}>
          {statusLine}
          {idLine}
          {title}
          <Box sx={{ mt: 1.5 }}>{actions}</Box>
          {facts}
          {description}
        </Box>
        <Box sx={{ flex: '1 1 380px', maxWidth: { lg: 520 }, minWidth: 0 }}>{aiSummary}</Box>
      </Box>
    </Box>
  );
}
